import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  House,
  CalendarBlank,
  ListChecks,
  SignOut,
  List,
  X,
  WifiHigh,
  WifiSlash,
  ArrowsClockwise,
  Bell,
} from "phosphor-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { useTranslation } from "../hooks/useTranslation";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TechLayout({ children, user, tenant }) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Navigation items with translated labels
  const navItems = [
    { path: "/tech", label: t("Today"), icon: House, exact: true },
    { path: "/tech/visits", label: t("My Visits"), icon: ListChecks },
  ];

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Check for pending sync items
  useEffect(() => {
    const checkPendingSync = () => {
      try {
        const queue = JSON.parse(localStorage.getItem("offlineQueue") || "[]");
        setPendingSync(queue.length);
      } catch (e) {
        setPendingSync(0);
      }
    };
    checkPendingSync();
    window.addEventListener("storage", checkPendingSync);
    return () => window.removeEventListener("storage", checkPendingSync);
  }, []);

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Body scroll lock for mobile sidebar - EXACT MATCH to AppLayout
  // Uses data attribute to signal active lock
  useEffect(() => {
    if (sidebarOpen) {
      const scrollY = window.scrollY;
      document.body.dataset.sidebarScrollLockY = String(scrollY);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll';
      
      return () => {
        delete document.body.dataset.sidebarScrollLockY;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [sidebarOpen]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    window.location.href = "/";
  };

  // Robust close function that works on iOS
  const closeMenu = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSidebarOpen(false);
  };

  const NavLink = ({ item }) => {
    const isActive = item.exact 
      ? location.pathname === item.path
      : location.pathname.startsWith(item.path);
    const Icon = item.icon;

    const handleNavClick = (e) => {
      // Close menu immediately on touch/click
      setSidebarOpen(false);
    };

    return (
      <Link
        to={item.path}
        onClick={handleNavClick}
        onTouchEnd={handleNavClick}
        className={`flex items-center gap-3 px-4 py-4 text-base font-medium transition-all ${
          isActive
            ? "bg-[#FF6600] text-white"
            : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
        }`}
        style={{ 
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation'
        }}
        data-testid={`tech-nav-${item.label.toLowerCase().replace(" ", "-")}`}
      >
        <Icon size={24} weight={isActive ? "fill" : "regular"} />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b-2 border-zinc-200 z-40 flex items-center justify-between px-4">
        <button
          type="button"
          onMouseDown={() => setSidebarOpen(true)}
          onTouchEnd={(e) => { e.preventDefault(); setSidebarOpen(true); }}
          className="p-3 hover:bg-zinc-100 active:bg-zinc-200 rounded-sm"
          data-testid="tech-mobile-menu-btn"
          aria-label="Open menu"
        >
          <List size={24} weight="bold" />
        </button>

        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-heading text-xs uppercase tracking-wider">
            Tech Portal
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          {/* Sync Status */}
          {!isOnline ? (
            <div className="p-2 text-amber-600">
              <WifiSlash size={20} />
            </div>
          ) : pendingSync > 0 ? (
            <div className="p-2 text-blue-600 relative">
              <ArrowsClockwise size={20} className="animate-spin" />
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                {pendingSync}
              </span>
            </div>
          ) : (
            <div className="p-2 text-emerald-600">
              <WifiHigh size={20} />
            </div>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2" data-testid="tech-user-menu-mobile">
                <div className="w-8 h-8 rounded-full bg-[#FF6600] text-white flex items-center justify-center font-medium text-sm">
                  {user?.name?.charAt(0) || "T"}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs text-zinc-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <SignOut className="mr-2" size={16} />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Sidebar Container - EXACT MATCH to AppLayout structure */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Overlay - Simple approach matching AppLayout */}
          <div
            className="absolute inset-0 bg-black/50"
            onMouseDown={closeMenu}
            onTouchEnd={closeMenu}
            data-testid="tech-sidebar-overlay"
          />

          {/* Sidebar */}
          <aside
            className="absolute top-0 left-0 h-full w-72 bg-white border-r-2 border-zinc-200"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            {/* Logo */}
            <div className="h-20 flex items-center justify-between px-4 border-b-2 border-zinc-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FF6600] text-white flex items-center justify-center">
                  <CalendarBlank size={24} weight="bold" />
                </div>
                <div>
                  <p className="font-heading font-bold text-lg">TECH PORTAL</p>
                  <p className="text-xs text-zinc-500">Free Service Pro</p>
                </div>
              </div>
              {/* Close Button - iOS-compatible matching AppLayout */}
              <button
                type="button"
                onMouseDown={closeMenu}
                onTouchEnd={closeMenu}
                className="lg:hidden w-11 h-11 flex items-center justify-center hover:bg-zinc-100 active:bg-zinc-200 rounded-lg"
                aria-label="Close menu"
                data-testid="tech-close-sidebar-btn"
              >
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Company Name */}
            <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50">
              <p className="font-heading text-sm font-semibold text-zinc-900 uppercase tracking-tight truncate">
                {tenant?.company_name || "Loading..."}
              </p>
            </div>

            {/* Navigation */}
            <nav className="flex flex-col h-[calc(100%-10rem)] justify-between py-4 overflow-y-auto">
              <div className="space-y-1">
                {navItems.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>

              <div className="px-4 space-y-2">
                {/* Online Status */}
                <div className={`p-3 rounded-sm ${isOnline ? "bg-emerald-50" : "bg-amber-50"}`}>
                  <div className="flex items-center gap-2">
                    {isOnline ? (
                      <>
                        <WifiHigh size={20} className="text-emerald-600" />
                        <span className="text-sm text-emerald-700 font-medium">{t("Online")}</span>
                      </>
                    ) : (
                      <>
                        <WifiSlash size={20} className="text-amber-600" />
                        <span className="text-sm text-amber-700 font-medium">{t("Offline Mode")}</span>
                      </>
                    )}
                  </div>
                  {pendingSync > 0 && (
                    <p className="text-xs text-zinc-500 mt-1">
                      {pendingSync} {t("item(s) pending sync")}
                    </p>
                  )}
                </div>

                {/* User Info */}
                <div className="p-3 bg-zinc-50 rounded-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#FF6600] text-white flex items-center justify-center font-medium">
                      {user?.name?.charAt(0) || "T"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{user?.name}</p>
                      <p className="text-xs text-zinc-500 truncate">{t("Technician")}</p>
                    </div>
                  </div>
                </div>

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-3 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                >
                  <SignOut size={20} />
                  <span className="font-medium">{t("Logout")}</span>
                </button>
              </div>
            </nav>
          </aside>
        </div>
      )}

      {/* Desktop Sidebar - Always visible on large screens */}
      <aside className="hidden lg:block fixed top-0 left-0 h-full w-72 bg-white border-r-2 border-zinc-200 z-30">
        {/* Logo */}
        <div className="h-20 flex items-center px-4 border-b-2 border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF6600] text-white flex items-center justify-center">
              <CalendarBlank size={24} weight="bold" />
            </div>
            <div>
              <p className="font-heading font-bold text-lg">TECH PORTAL</p>
              <p className="text-xs text-zinc-500">Free Service Pro</p>
            </div>
          </div>
        </div>

        {/* Company Name */}
        <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50">
          <p className="font-heading text-sm font-semibold text-zinc-900 uppercase tracking-tight truncate">
            {tenant?.company_name || "Loading..."}
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col h-[calc(100%-10rem)] justify-between py-4 overflow-y-auto">
          <div className="space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>

          <div className="px-4 space-y-2">
            {/* Online Status */}
            <div className={`p-3 rounded-sm ${isOnline ? "bg-emerald-50" : "bg-amber-50"}`}>
              <div className="flex items-center gap-2">
                {isOnline ? (
                  <>
                    <WifiHigh size={20} className="text-emerald-600" />
                    <span className="text-sm text-emerald-700 font-medium">{t("Online")}</span>
                  </>
                ) : (
                  <>
                    <WifiSlash size={20} className="text-amber-600" />
                    <span className="text-sm text-amber-700 font-medium">{t("Offline Mode")}</span>
                  </>
                )}
              </div>
              {pendingSync > 0 && (
                <p className="text-xs text-zinc-500 mt-1">
                  {pendingSync} {t("item(s) pending sync")}
                </p>
              )}
            </div>

            {/* User Info */}
            <div className="p-3 bg-zinc-50 rounded-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FF6600] text-white flex items-center justify-center font-medium">
                  {user?.name?.charAt(0) || "T"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{user?.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{t("Technician")}</p>
                </div>
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-3 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
            >
              <SignOut size={20} />
              <span className="font-medium">{t("Logout")}</span>
            </button>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 pt-16 lg:pt-0 min-h-screen">
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 bg-white border-b-2 border-zinc-200 items-center justify-between px-8">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-lg font-semibold uppercase tracking-tight">
              {navItems.find((i) => i.exact ? location.pathname === i.path : location.pathname.startsWith(i.path))?.label || "Tech Portal"}
            </h1>
            <Badge variant="outline" className="font-heading text-xs uppercase tracking-wider">
              {tenant?.company_name}
            </Badge>
          </div>
          <div className="flex items-center gap-4">
            {/* Online indicator */}
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${isOnline ? "bg-emerald-50" : "bg-amber-50"}`}>
              {isOnline ? (
                <>
                  <WifiHigh size={16} className="text-emerald-600" />
                  <span className="text-xs text-emerald-700 font-medium">{t("Online")}</span>
                </>
              ) : (
                <>
                  <WifiSlash size={16} className="text-amber-600" />
                  <span className="text-xs text-amber-700 font-medium">{t("Offline")}</span>
                </>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-100 rounded-sm" data-testid="tech-user-menu-desktop">
                  <div className="w-8 h-8 rounded-full bg-[#FF6600] text-white flex items-center justify-center font-medium text-sm">
                    {user?.name?.charAt(0) || "T"}
                  </div>
                  <span className="text-sm font-medium">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium text-sm">{user?.name}</p>
                  <p className="text-xs text-zinc-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <SignOut className="mr-2" size={16} />
                  {t("Logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Offline Banner */}
        {!isOnline && (
          <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm font-medium">
            {t("You're offline. Changes will sync when connection is restored.")}
          </div>
        )}

        {/* Page Content */}
        <div className="p-4 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
